import type { DoorPick, DoorProposalResponse } from '@adhd/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { useSession } from '../lib/session.js';
import { CrisisNoticeCard, type CrisisNotice } from './CrisisNotice.js';
import { PickLegend } from './WorksheetEditors.js';
import { VoiceRecorder, type Recording } from './VoiceRecorder.js';

/**
 * The door: say anything, and the app files it, with receipts.
 * Release 4 of 2.0 (docs/PRD-2.0.md §3).
 *
 * The flow is always the same and always starts with the entry: what he says
 * is SAVED FIRST, as a journal entry, at every rung. Then, at the rungs that
 * read entries and with the switch on, the sorter proposes a filing and this
 * shows it as receipts — each pick dashed, each quoting the words it came
 * from, × takes one off (and the app learns), File it writes exactly what is
 * left. If the sorter is off or the rung is too low, the entry is saved and
 * four chips offer the forms. Nothing is written until he says so, and the
 * slider on Today is always the check-in that needs none of this.
 *
 * Slash commands: a line starting with / opens the one-line form instead of
 * saving anything: /dose /mark /worksheet /journal /note.
 *
 * "Ask before filing" (Settings → AI, 14 Oct 2026): on, the above. Off, the
 * picks are filed the moment the sorter answers and the same receipts are
 * shown already written — × then takes that one row back out (and the app
 * learns, as before). A mark that was his already is left alone and says so.
 */
type Stage = 'say' | 'saving' | 'reading' | 'receipts' | 'filed' | 'plain';

/** "Tuesday" for a YYYY-MM-DD, at local midday so no zone shifts it. */
const weekday = (day: string): string => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' });

export function Door({
  prompt = 'Say anything…',
  day,
  onSaved,
  onFiled,
  autoOpen = false,
}: {
  prompt?: string;
  /**
   * The day the entry belongs to. Before 4am Today is still yesterday's night
   * (the app's own boundary), and an entry said at 00:53 files there, not under
   * the new date — a journal on 13 Oct 2026 went to the wrong day because this
   * was missing. The sheet says which day, and "today instead" is the way out.
   */
  day?: string;
  /** An entry was saved; Today's lists should refresh. */
  onSaved?: () => void;
  /** Rows were filed; Today's figures should refresh. */
  onFiled?: () => void;
  /** Open the sheet at once (the night face). */
  autoOpen?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const { canTranscribe } = useAi();
  const { user } = useSession();
  const asks = user?.doorAsks ?? true;
  const [open, setOpen] = useState(autoOpen);
  const [mode, setMode] = useState<'speak' | 'write'>(canTranscribe ? 'speak' : 'write');
  const [text, setText] = useState('');
  const [stage, setStage] = useState<Stage>('say');
  const [entryId, setEntryId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<DoorProposalResponse | null>(null);
  const [kept, setKept] = useState<Set<string>>(new Set());
  const [answer, setAnswer] = useState<'draft' | 'entry' | null>(null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filedNote, setFiledNote] = useState<string | null>(null);
  /** With asking off: what the immediate filing said about each pick (id → note), once it has happened. */
  const [auto, setAuto] = useState<Map<string, string | undefined> | null>(null);
  const [underToday, setUnderToday] = useState(false);
  const todayKey = new Date().toLocaleDateString('en-CA');
  const lateNight = Boolean(day && day !== todayKey);
  /*
   * Which day the entry goes under: the night it belongs to before 4am,
   * decided when the entry is SAVED rather than when the screen
   * was last drawn (22 Oct 2026). An entry begun at 11:59pm is saved after
   * midnight; by then the calendar says the next day and `day` still says the
   * night it belongs to, so it is filed there.
   */
  const fileUnder = (): string | undefined => {
    const nowKey = new Date().toLocaleDateString('en-CA');
    return day && day !== nowKey && !underToday ? day : undefined;
  };
  const pollRef = useRef<number | null>(null);

  useEffect(() => () => { if (pollRef.current) window.clearTimeout(pollRef.current); }, []);

  function reset(): void {
    setText('');
    setStage('say');
    setEntryId(null);
    setProposal(null);
    setKept(new Set());
    setAnswer(null);
    setError(null);
    setFiledNote(null);
    setAuto(null);
  }

  /** The text was saved; now ask the sorter, waiting for a transcript if it is still being made. */
  async function readBack(id: string): Promise<void> {
    setStage('reading');
    const deadline = Date.now() + 90_000;
    const tick = async (): Promise<void> => {
      try {
        const entry = await api.getJournalEntry(id);
        if (entry.transcriptStatus === 'done' || entry.transcriptStatus === 'skipped' || entry.transcript) {
          if (!entry.transcript) {
            setStage('plain');
            return;
          }
          const p = await api.doorPropose(id);
          setProposal(p);
          if (p.off || (p.picks.length === 0 && !p.question && p.earlierReads.length === 0)) {
            setStage('plain');
          } else {
            setKept(new Set(p.picks.map((k) => k.id)));
            if (!asks && p.picks.length > 0) {
              // Filed now, as proposed; the receipts arrive already written.
              const r = await api.doorFile(id, p.picks.map((k) => ({ id: k.id, payload: k.payload })));
              setAuto(new Map(r.filed.filter((f) => f.ok).map((f) => [f.id, f.note])));
              onFiled?.();
            }
            setStage('receipts');
          }
          return;
        }
        if (entry.transcriptStatus === 'failed' || Date.now() > deadline) {
          setStage('plain');
          return;
        }
        pollRef.current = window.setTimeout(() => void tick(), 2000);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not read that back.');
        setStage('plain');
      }
    };
    await tick();
  }

  async function saveText(justSave = false): Promise<void> {
    const t = text.trim();
    if (!t) return;
    if (t.startsWith('/')) {
      slash(t);
      return;
    }
    setStage('saving');
    setError(null);
    try {
      const under = fileUnder();
      const entry = await api.createTextEntry({ text: t, ...(under ? { day: under } : {}) });
      setEntryId(entry.id);
      onSaved?.();
      if (entry.crisis) {
        setCrisis(entry.crisis);
        setStage('plain');
        return;
      }
      if (justSave) {
        setStage('plain');
        return;
      }
      await readBack(entry.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
      setStage('say');
    }
  }

  async function saveRecording(rec: Recording, justSave = false): Promise<void> {
    setStage('saving');
    setError(null);
    try {
      const under = fileUnder();
      const entry = await api.uploadRecording(rec.blob, { kind: 'daily_journal', durationSeconds: rec.durationSeconds, ...(under ? { day: under } : {}) });
      setEntryId(entry.id);
      onSaved?.();
      if (justSave) {
        setStage('plain');
        return;
      }
      await readBack(entry.id);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.message} Your recording is still here.` : "Couldn't save that recording.");
      setStage('say');
    }
  }

  function slash(t: string): void {
    const cmd = t.slice(1).split(/\s+/)[0]?.toLowerCase();
    if (cmd === 'dose' || cmd === 'doses' || cmd === 'meds') navigate('/medications');
    else if (cmd === 'worksheet' || cmd === 'thought') navigate('/thought-record');
    else if (cmd === 'journal') navigate('/journal');
    else if (cmd === 'event' || cmd === 'thread') navigate('/history');
    else setError('Slash commands: /dose, /worksheet, /journal, /event.');
  }

  function takeOff(p: DoorPick): void {
    setKept((k) => {
      const n = new Set(k);
      n.delete(p.id);
      return n;
    });
    void api.doorCorrect(p.kind, p.key).catch(() => {});
    // Already written: the row goes too.
    if (auto && entryId && auto.has(p.id)) {
      void api.doorUnfile(entryId, { id: p.id, payload: p.payload }).then(() => onFiled?.()).catch(() => setError('Could not take that back out.'));
    }
  }

  async function file(): Promise<void> {
    if (!proposal || !entryId) return;
    setStage('saving');
    try {
      const picks = proposal.picks.filter((p) => kept.has(p.id)).map((p) => ({ id: p.id, payload: p.payload }));
      const r = await api.doorFile(entryId, picks);
      const n = r.filed.filter((f) => f.ok).length;
      setFiledNote(n === 0 ? 'Nothing filed; the entry is saved.' : `Filed ${n} ${n === 1 ? 'thing' : 'things'} with the entry.`);
      onFiled?.();
      setStage('filed');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not file that.');
      setStage('receipts');
    }
  }

  if (!open) {
    return (
      <button type="button" className="say" onClick={() => setOpen(true)} aria-expanded={false}>
        <span className="say-mic" aria-hidden="true">●</span>
        <span className="say-ph">{prompt}</span>
        <span className="say-type">or type</span>
      </button>
    );
  }

  return (
    <section className="door card stack-sm stack" aria-label="Say anything">
      {crisis && <CrisisNoticeCard notice={crisis} onDismiss={() => setCrisis(null)} />}

      {stage === 'say' && (
        <>
          <div className="row-between">
            <strong className="door-h">{prompt}</strong>
            {canTranscribe && (
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button type="button" className="chip" aria-pressed={mode === 'speak'} onClick={() => setMode('speak')}>Speak</button>
                <button type="button" className="chip" aria-pressed={mode === 'write'} onClick={() => setMode('write')}>Write</button>
              </div>
            )}
          </div>
          {lateNight && day && (
            <p className="faint" style={{ margin: 0 }}>
              {underToday ? 'Filed under today.' : `Filed under ${weekday(day)}, the night it belongs to.`}{' '}
              <button type="button" className="linkish linkish-quiet" onClick={() => setUnderToday((v) => !v)}>{underToday ? `${weekday(day)} instead` : 'today instead'}</button>
            </p>
          )}
          {mode === 'speak' && canTranscribe ? (
            <VoiceRecorder onComplete={(rec) => void saveRecording(rec)} />
          ) : (
            <>
              <label htmlFor="door-text" className="sr-only">{prompt}</label>
              <textarea
                id="door-text"
                className="textarea"
                rows={3}
                placeholder="Anything, in any order. A sentence is fine. Start with / for a form."
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn-primary grow" disabled={!text.trim()} onClick={() => void saveText(false)}>
                  Save and file
                </button>
                <button type="button" className="btn btn-ghost" disabled={!text.trim()} onClick={() => void saveText(true)} title="An entry, no filing, no model">
                  Just save it
                </button>
              </div>
            </>
          )}
          {!autoOpen && (
            <button type="button" className="linkish linkish-quiet" onClick={() => setOpen(false)}>Not now</button>
          )}
        </>
      )}

      {stage === 'saving' && <p className="muted" style={{ margin: 0 }}><span className="spinner" /> Saving…</p>}
      {stage === 'reading' && <p className="muted" style={{ margin: 0 }}><span className="spinner" /> Saved. Reading it back…</p>}

      {stage === 'plain' && (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Saved as an entry{entryId ? <> — <Link to={`/journal/${entryId}`}>open it</Link></> : null}.
            {proposal?.off === 'rung' && ' AI is below “write my entries up”, so nothing was filed for you.'}
            {proposal?.off === 'switch' && ' Filing is switched off in Settings.'}
          </p>
          <div className="chips">
            <Link className="chip" to="/medications">Dose</Link>
            <button type="button" className="chip" onClick={() => navigate('/thought-record')}>Worksheet</button>
            <Link className="chip" to={entryId ? `/journal/${entryId}` : '/journal'}>Mark the day</Link>
            <Link className="chip" to={entryId ? `/journal/${entryId}` : '/people'}>People</Link>
          </div>
          <button type="button" className="linkish linkish-quiet" onClick={reset}>Say something else</button>
        </>
      )}

      {stage === 'receipts' && proposal && (
        <>
          <div className="row-between">
            <strong className="door-h">{auto ? 'Filed with the entry' : 'Filed as'}</strong>
            <span className="faint">{auto ? '× takes one back out' : 'nothing is written until you file it'}</span>
          </div>
          {proposal.picks.length > 0 && <PickLegend appLabel="the app’s pick" note={auto ? '× takes one back out; the app learns.' : '× takes one off; the app learns.'} />}
          <ul className="receipts">
            {proposal.picks.map((p) => {
              const on = kept.has(p.id);
              return (
                <li key={p.id} className={`rec${on ? '' : ' rec-off'}`}>
                  <span className="rec-kind">{p.kind === 'mood' ? 'Right now' : p.kind === 'dose' ? 'Dose' : p.kind === 'mark' ? 'Mark' : 'With'}</span>
                  <span className="rec-what">{p.label}</span>
                  {auto?.get(p.id) === 'already marked' ? (
                    <span className="rec-x faint" style={{ fontSize: 'var(--text-xs)' }}>already yours</span>
                  ) : on ? (
                    <button type="button" className="rec-x" aria-label={auto ? `Take ${p.label} back out` : `Take off ${p.label}`} onClick={() => takeOff(p)}>×</button>
                  ) : (
                    <span className="rec-x faint">{auto ? 'taken out' : 'off'}</span>
                  )}
                  <span className="rec-why">because you said <q>{p.quote}</q></span>
                </li>
              );
            })}
            <li className="rec rec-solid">
              <span className="rec-kind">Entry</span>
              <span className="rec-what">The whole thing, in your words</span>
              <span className="rec-x" />
              <span className="rec-why">always</span>
            </li>
          </ul>
          {proposal.question && (
            <div className="door-ask">
              <span>
                You said <q>{proposal.question.quote}</q>. Draft a worksheet to work it through ({proposal.question.format}), or keep it in the entry?
              </span>
              <div className="chips">
                <button type="button" className="chip" aria-pressed={answer === 'draft'} onClick={() => setAnswer('draft')}>Draft it</button>
                <button type="button" className="chip" aria-pressed={answer === 'entry'} onClick={() => setAnswer('entry')}>Just the entry</button>
              </div>
            </div>
          )}
          {proposal.earlierReads.length > 0 && (
            <div className="before-card">
              <span className="before-k">You've been here before</span>
              {proposal.earlierReads.map((r) => (
                <blockquote key={r.recordId} className="before-own">
                  <p className="faint" style={{ margin: 0 }}>You thought <q>{r.thought}</q></p>
                  <p className="reading" style={{ margin: 0 }}>{r.response}</p>
                  <footer className="faint">
                    {new Date(`${r.day}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    {r.beliefBefore !== null && r.beliefAfter !== null ? ` · belief ${r.beliefBefore}% → ${r.beliefAfter}%` : ''}
                    {' · '}<Link to={`/thought-record/${r.recordId}`}>the worksheet</Link>
                  </footer>
                </blockquote>
              ))}
              <span className="faint">Your own fairer read, word for word. Off in Settings if it ever lands wrong.</span>
            </div>
          )}
          {auto ? (
            <>
              {answer === 'draft' && entryId && (
                <p className="muted" style={{ margin: 0 }}>
                  To draft the worksheet, open the entry and tap “Have a look” — it fills the steps from your words.
                </p>
              )}
              <button type="button" className="linkish linkish-quiet" onClick={reset}>Say something else</button>
            </>
          ) : (
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => void file()}>
              File it
            </button>
          )}
          {error && <p className="faint" role="alert">{error}</p>}
        </>
      )}

      {stage === 'filed' && (
        <>
          <p className="muted" style={{ margin: 0 }}>
            {filedNote}{entryId ? <> <Link to={`/journal/${entryId}`}>Open the entry</Link>.</> : null}
          </p>
          {answer === 'draft' && entryId && (
            <p className="muted" style={{ margin: 0 }}>
              To draft the worksheet, open the entry and tap “Have a look” — it fills the steps from your words.
            </p>
          )}
          <button type="button" className="linkish linkish-quiet" onClick={reset}>Say something else</button>
        </>
      )}

      {stage === 'say' && error && <p className="faint" role="alert">{error}</p>}
    </section>
  );
}
